import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  OutlinedInput,
  Select,
  styled,
  Switch,
  Typography,
  withStyles,
} from "@material-ui/core";
import { formatPhoneNumber } from "../ui/Formatters";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "core/selectors";
import { getAccount } from "account/selectors";
import { isFeatureEnabled } from "PoshUtils";
import PhoneTextFieldWithFlag from "ui/PhoneTextFieldWithFlag";
import { createConditionalCallForwarding } from "./actions";
import LoadingButton from "ui/LoadingButton";

const CallFowardingOptionContainer = styled("div")(({ theme }) => ({
  padding: "5px 0",
  margin: "10px 0",
}));
const Row = styled("div")(({ theme, spaceX, wrap }) => ({
  display: "flex",
  width: "100%",
  justifyContent: "flex-start",
  flexDirection: "column",
  alignItems: "center",
  // margin: '0 5px',
  "& > *:not(:first-child):not(:last-child)": {
    margin: `0px ${spaceX ? "5px" : "0px"}`,
  },
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    flexWrap: wrap ? "wrap" : "nowrap",
    alignItems: "center",
  },
}));
const Sofia = styled(Typography)(() => ({
  fontFamily: "Sofia",
}));
const CallForwardingFormContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  "& > *": {
    margin: "8px 0",
  },
}));
const PhoneNumberInput = styled("div")(() => ({
  width: "250px",
}));
const PurpleSwitch = styled(Switch)(({ theme }) => ({
  "& .MuiSwitch-switchBase": {
    color: theme.brandingColors.rubyDarkPurple,
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: theme.brandingColors.rubyDarkPurple,
  },
}));
const styles = (theme) => ({
  headings: {
    fontFamily: "Sofia-bold",
    fontSize: 22,
  },
  details: {
    fontFamily: "Sofia",
  },
  minutesPlayed: {
    fontFamily: "Sofia",
    fontSize: 14,
    margin: 7,
  },
});
function CallFowarding() {
  const account = useSelector(getAccount);
  return (
    <div>
      <Typography
        style={{
          fontFamily: "Sofia-bold",
          fontSize: 22,
        }}
      >
        Call Forwarding
      </Typography>
      {!isFeatureEnabled(account, "ALLOW_CALL_FORWARDING") ? (
        <Typography>
          This account does not have access to Ruby Call Forwarding. Give us a
          call at 866-611-7829 or email{" "}
          <span
            style={{
              fontFamily: "Sofia-bold",
              fontweight: 18,
              textDecoration: "underline",
            }}
          >
            <a href="mailto:staff@ruby.com" style={{ color: "#000" }}>
              staff@ruby.com
            </a>
          </span>{" "}
          to get set up.
        </Typography>
      ) : account?.phoneNumbers?.length ? (
        account?.phoneNumbers?.map((number, _) => (
          <div key={number.phoneNumberId}>
            <CallForwardingOption number={number} />
            {_ !== account?.phoneNumbers?.length - 1 && <Divider />}
          </div>
        ))
      ) : (
        <Sofia>No active numbers found</Sofia>
      )}
    </div>
  );
}

function CallForwardingOption({ number, account }) {
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const [isHandlingCallForwarding, setIsHandlingCallForwarding] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [callForwardingEnabled, setCallForwardingEnabled] = useState(false);
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState(null);
  const [shouldForwardToRuby, setShouldForwardToRuby] = useState(false);
  const [customNumber, setCustomNumber] = useState(null);
  useEffect(() => {
    const init = () => {
      // This number already has call forwarding enabled
      if (number.handling === "FORWARD") {
        const phoneNumberId = user?.selfContact[0]?.contactMethods?.find(
          (item) => item.phoneNumber === number.forwardNumber
        )?.contactMethodId;
        if (phoneNumberId) {
          setSelectedPhoneNumberId(phoneNumberId);
        } else {
          setCustomNumber(number.forwardNumber);
        }
        if (number.forwardToAgent === "Y") {
          setShouldForwardToRuby(true);
        }
        setCallForwardingEnabled(true);
      } else {
        const firstPhoneNumber = user?.selfContact?.[0]?.contactMethods?.find(
          (item) => item.contactMethodType === "PHONE"
        );
        // Set up the inital state if phone number is found (Or else default is custom number)
        if (firstPhoneNumber) {
          setSelectedPhoneNumberId(firstPhoneNumber.contactMethodId);
        } else {
          setCustomNumber("");
        }
      }
    };
    init();
  }, [number, user]);
  const handleConcatMethodSelect = (e) => {
    if (e?.target?.value === "custom") {
      setCustomNumber("");
      setSelectedPhoneNumberId(null);
    } else {
      setSelectedPhoneNumberId(e.target.value);
      setCustomNumber(null);
    }
  };
  const handleEnableTrigger = (e) => {
    if (!e.target.checked) {
      setShowConfirm(true);
    } else {
      setCallForwardingEnabled(true);
    }
  };
  const errorHandler = (statusText, status, content) => {
    console.log("An error occured", { statusText, status, content });
    setIsHandlingCallForwarding(false);
    setShowConfirm(false);
    setDisableLoading(false);
  };
  const handleCallForwarding = () => {
    setIsHandlingCallForwarding(true);
    const update = number?.handling === "FORWARD";
    if (customNumber) {
      dispatch(
        createConditionalCallForwarding(
          number.phoneNumberId,
          customNumber,
          "FORWARD",
          shouldForwardToRuby ? "Y" : "N",
          update,
          () => {
            console.log("The conditional call forwarding is completed");
            setIsHandlingCallForwarding(false);
          },
          errorHandler
        )
      );
    } else {
      const phoneNumber = user?.selfContact[0]?.contactMethods?.filter(
        (item) => item.contactMethodId === selectedPhoneNumberId
      )[0]?.phoneNumber;
      dispatch(
        createConditionalCallForwarding(
          number.phoneNumberId,
          phoneNumber,
          "FORWARD",
          shouldForwardToRuby ? "Y" : "N",
          update,
          () => {
            console.log("The conditional call forwarding is completed");
            setIsHandlingCallForwarding(false);
          },
          errorHandler
        )
      );
    }
  };
  const disableCallForwarding = () => {
    dispatch(
      createConditionalCallForwarding(
        number.phoneNumberId,
        undefined,
        "ANSWER",
        "N",
        false,
        () => {
          setCallForwardingEnabled(false);
          setSelectedPhoneNumberId(null);
          setShouldForwardToRuby(false);
          setCustomNumber(null);
          setShowConfirm(false);
          setDisableLoading(false);
        },
        errorHandler
      )
    );
  };
  return (
    <CallFowardingOptionContainer>
      <FormControlLabel
        control={
          <PurpleSwitch
            checked={callForwardingEnabled}
            onChange={handleEnableTrigger}
          />
        }
        label={<Sofia>{formatPhoneNumber(number.phoneNumber)}</Sofia>}
        labelPlacement="end"
      />
      {callForwardingEnabled && (
        <CallForwardingFormContainer>
          <Row
            wrap
            style={{ borderTop: "1px solid #00000030", paddingTop: "10px" }}
          >
            <Sofia
              style={{
                textWrap: "nowrap",
                fontWeight: 600,
                margin: "0 0 15px 0",
              }}
            >
              Forward calls from&nbsp;
              {/* <span> </span> */}
            </Sofia>
            <Row spaceX>
              <Sofia>{formatPhoneNumber(number.phoneNumber)}</Sofia>
              <Sofia>&nbsp;to&nbsp;</Sofia>
              <PhoneNumberInput>
                <Select
                  style={{ width: "100%" }}
                  value={selectedPhoneNumberId || "custom"}
                  onChange={handleConcatMethodSelect}
                  input={<OutlinedInput />}
                >
                  {user?.selfContact[0]?.contactMethods
                    ?.filter((item) => item.contactMethodType === "PHONE")
                    ?.map((num) => (
                      <MenuItem
                        key={num.contactMethodId}
                        value={num.contactMethodId}
                      >
                        <Sofia>{formatPhoneNumber(num.phoneNumber)}</Sofia>
                      </MenuItem>
                    ))}
                  <MenuItem key={"custom"} value={"custom"}>
                    <Sofia>Custom Number</Sofia>
                  </MenuItem>
                </Select>
              </PhoneNumberInput>
              {typeof customNumber === "string" && (
                <PhoneNumberInput>
                  <PhoneTextFieldWithFlag
                    value={customNumber}
                    onPhoneNumberChange={(phoneNumber) => {
                      setCustomNumber(phoneNumber);
                    }}
                  />
                </PhoneNumberInput>
              )}
            </Row>
          </Row>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                style={{ color: "#7F2062" }}
                checked={shouldForwardToRuby}
                onChange={(e) => {
                  setShouldForwardToRuby(e?.target?.checked);
                }}
              />
            }
            label={<Sofia>If no answer, forward to Ruby </Sofia>}
          />
          <LoadingButton
            loading={isHandlingCallForwarding}
            disabled={
              ((!customNumber ||
                (customNumber && customNumber.length !== 12)) &&
                !selectedPhoneNumberId) ||
              isHandlingCallForwarding
            }
            style={{ width: "max-content" }}
            onClick={handleCallForwarding}
          >
            Save
          </LoadingButton>
        </CallForwardingFormContainer>
      )}
      {/* Confirmation Modal */}
      <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
        <DialogTitle>Call Forwarding</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to turn off call forwarding for{" "}
            {formatPhoneNumber(number.phoneNumber)}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            loading={disableLoading}
            disabled={disableLoading}
            onClick={disableCallForwarding}
          >
            Yes
          </LoadingButton>
          <LoadingButton
            disabled={disableLoading}
            onClick={() => {
              setShowConfirm(false);
            }}
          >
            No
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </CallFowardingOptionContainer>
  );
}

export default withStyles(styles)(CallFowarding);
